/**
 * Server naming: the single source of truth on this side. Every server name the wizard shows or submits is built here, and no other file
 * spells out a name pattern. Mirrors the backend's config/naming.py and rules/naming.py (which are authoritative), the pipeline's
 * reservation step (reserve-traverse-names.sh) and Ansible, which verifies the name it is given. A contract test keeps the patterns the same.
 *
 * A name is NOT based on the Customer Code. It is a fixed part followed by a NUMBER:
 *
 *   Dedicated RDS   <env prefix>TRAVRDS<num>   PMTTRAVRDS39
 *   Dedicated DB    <env prefix>TRAVDB<num>     PMTTRAVDB13
 *   Dedicated App   <env letter>TRAV<num>APP01  PTRAV08APP01      (APP01 is fixed)
 *
 * `<env prefix>` is by environment type: production PMT, demo DMT, staging STG (dev DMT). `<num>` is the highest number that already exists
 * in that series among the Azure VMs of the environment's subscription, plus 1 (the highest, not the first gap; matched in any case, so
 * `pmttravrds38` counts), 01 when none exists, at least two digits, and never truncated past 99. Names are upper case and are Windows
 * computer names: at most 15 characters, which is checked, never truncated to.
 *
 * What the page shows is a PREVIEW. The pipeline reserves the real name just before Terraform runs, and the page says so.
 *
 * TO CHANGE A PATTERN, edit the constants and `SERVER_NAMING_RULES` below (and the backend's config/naming.py).
 */
import { environmentTypeOf, type EnvironmentType } from '@/data/environmentTypes'
import { productFamily, type ProductFamily, type ProductSubject } from '@/data/productLines'

/** Windows computer names are limited to 15 characters. */
export const HOST_NAME_MAX = 15
/** Azure SQL (PaaS) server names are 3 to 63 characters: lower-case letters, digits and hyphens. */
export const AZURE_SQL_SERVER_NAME_MIN = 3
export const AZURE_SQL_SERVER_NAME_MAX = 63

/** Customer Codes: three digits, 100 to 999. They are still asked for (tags, the state file, the run's lock) but no name uses them. */
export const CUSTOMER_NUMBER_MIN = 100
export const CUSTOMER_NUMBER_MAX = 999

/** The environment part of an RDS or DB name, by environment type (dev is listed for an id that is itself a type). */
export const ENV_TYPE_NAME_PREFIX: Readonly<Record<string, string>> = Object.freeze({ production: 'PMT', demo: 'DMT', dev: 'DMT', staging: 'STG' })
/** The letter before TRAV in an App name, by environment type: P for production. [CONFIRM: D for demo and S for staging are assumed] */
export const ENV_TYPE_APP_LETTER: Readonly<Record<string, string>> = Object.freeze({ production: 'P', demo: 'D', dev: 'D', staging: 'S' })
/** The App name's fixed ending. */
export const APP_NAME_SUFFIX = 'APP01'
/** The number is zero-padded to this many digits at least (01 … 99, then 100). */
export const SEQUENCE_DIGITS = 2

export type ServerRole = 'app' | 'rds' | 'db'

export const SERVER_ROLE_LABELS: Readonly<Record<ServerRole, string>> = Object.freeze({ app: 'App server', rds: 'RDS server', db: 'SQL server' })

interface RoleRule {
  /** The text before the number: `{prefix}` is the environment prefix, `{letter}` the App environment letter. */
  before: string
  after?: string
  /** True for a pattern that was not supplied and stands in until it is. */
  placeholder?: boolean
}

interface ServerNamingRule {
  families: ProductFamily[]
  roles: Partial<Record<ServerRole, RoleRule>>
}

const ROLES: Readonly<Record<ServerRole, RoleRule>> = Object.freeze({
  rds: { before: '{prefix}TRAVRDS' },
  db: { before: '{prefix}TRAVDB' },
  app: { before: '{letter}TRAV', after: APP_NAME_SUFFIX },
})

export const SERVER_NAMING_RULES: readonly ServerNamingRule[] = Object.freeze([
  { families: ['traverse'], roles: { ...ROLES } },
  {
    // Process Pro Global: no pattern of its own was supplied, so it follows the Traverse conventions until one is. [CONFIRM]
    families: ['process-pro'],
    roles: {
      rds: { ...ROLES.rds, placeholder: true },
      db: { ...ROLES.db, placeholder: true },
      app: { ...ROLES.app, placeholder: true },
    },
  },
])

// ─── Customer Code, products, host names ─────────────────────────────────────

export function isValidCustomerNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= CUSTOMER_NUMBER_MIN && value <= CUSTOMER_NUMBER_MAX
}

/** A product's lookup key: its slug (or name), lower-cased with everything but letters and digits removed. */
export function productKey(product: { slug?: string; name?: string } | string | null | undefined): string {
  const raw = typeof product === 'string' ? product : (product?.slug || product?.name || '')
  return raw.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** Valid as a Windows host name: letters, digits and hyphens, no leading or trailing hyphen, at most 15 characters. */
export function isValidHostName(name: string): boolean {
  return name.length >= 1 && name.length <= HOST_NAME_MAX && /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?$/.test(name)
}

/**
 * The Azure SQL (PaaS) form of a name: lower case, letters, digits and hyphens only, no leading or trailing hyphen,
 * truncated to 63 characters. Null if fewer than 3 valid characters remain.
 */
export function toAzureSqlServerName(name: string): string | null {
  const cleaned = name
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, AZURE_SQL_SERVER_NAME_MAX)
    .replace(/^-+|-+$/g, '')
  return cleaned.length >= AZURE_SQL_SERVER_NAME_MIN ? cleaned : null
}

// ─── Rules ───────────────────────────────────────────────────────────────────

function ruleFor(product: ProductSubject): ServerNamingRule | undefined {
  const family = productFamily(product)
  return family ? SERVER_NAMING_RULES.find((rule) => rule.families.includes(family)) : undefined
}

/** True when names are generated for the product. Without a rule the SQL server name has to be typed. */
export function hasNamingRule(product: ProductSubject): boolean {
  return ruleFor(product) !== undefined
}

/** True when the product's deployments need the Customer Code. Names no longer use it, but the pipeline does, so every product with a rule still asks. */
export function requiresCustomerCode(product: ProductSubject): boolean {
  return hasNamingRule(product)
}

/** True when the role's pattern is a placeholder, not one that was supplied. */
export function isPlaceholderPattern(product: ProductSubject, role: ServerRole): boolean {
  return ruleFor(product)?.roles[role]?.placeholder === true
}

/** The type of an environment: what the catalog says, or, for an id that is itself a type with a prefix (`dev`), that type. */
function typeOfEnvironment(environmentId: string): string | null {
  if (!environmentId) return null
  const known: EnvironmentType | null = environmentTypeOf({ id: environmentId })
  if (known) return known
  return Object.hasOwn(ENV_TYPE_NAME_PREFIX, environmentId) ? environmentId : null
}

/** The series of one role in one environment: `<before><number><after>`, for example `PMTTRAVRDS` and a number. */
export interface ServerNameSeries {
  role: ServerRole
  before: string
  after: string
  /** How the pattern is written in a note, for example `<env>TRAVRDS<nn>`. */
  pattern: string
}

export interface ServerNameInput {
  product: ProductSubject
  environmentId: string
  role: ServerRole
}

/** The series of a name, or why there is none. */
export function serverNameSeries(input: ServerNameInput): { ok: true; series: ServerNameSeries } | { ok: false; problem: string } {
  const rule = ruleFor(input.product)
  if (!rule) return { ok: false, problem: 'No server naming rule is configured for this product.' }
  const role = rule.roles[input.role]
  if (!role) return { ok: false, problem: `No ${SERVER_ROLE_LABELS[input.role].toLowerCase()} naming rule is configured for this product.` }
  const type = typeOfEnvironment(input.environmentId)
  if (!type) return { ok: false, problem: `The ${input.environmentId || 'chosen'} environment has no environment type, so no server name prefix can be chosen.` }
  const needsLetter = role.before.includes('{letter}')
  if (!Object.hasOwn(ENV_TYPE_NAME_PREFIX, type) || (needsLetter && !Object.hasOwn(ENV_TYPE_APP_LETTER, type))) {
    const listed = Object.entries(ENV_TYPE_NAME_PREFIX).map(([name, prefix]) => `${name} (${prefix})`).join(', ')
    return { ok: false, problem: `No server name prefix is configured for the ${type} environment type, so its names cannot be generated. Prefixes exist for: ${listed}.` }
  }
  const before = role.before.replace('{prefix}', ENV_TYPE_NAME_PREFIX[type]).replace('{letter}', ENV_TYPE_APP_LETTER[type] ?? '')
  const pattern = `${role.before.replace('{prefix}', '<env>').replace('{letter}', '<env>')}<nn>${role.after ?? ''}`
  return { ok: true, series: { role: input.role, before, after: role.after ?? '', pattern } }
}

const pad = (value: number) => String(value).padStart(SEQUENCE_DIGITS, '0')
const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** A number written as a name of the series: PMTTRAVRDS + 3 -> PMTTRAVRDS03. */
export const nameFor = (series: Pick<ServerNameSeries, 'before' | 'after'>, number: number): string => `${series.before}${pad(number)}${series.after}`

/** The highest number among the names that belong to the series, or 0 when there are none. Case-insensitive, whole names only. */
export function highestNumber(names: readonly string[], series: Pick<ServerNameSeries, 'before' | 'after'>): number {
  const pattern = new RegExp(`^${escapeRegExp(series.before)}(\\d+)${escapeRegExp(series.after)}$`, 'i')
  let highest = 0
  for (const name of names) {
    const match = pattern.exec(name.trim())
    if (match) highest = Math.max(highest, Number(match[1]))
  }
  return highest
}

/** What the service answers for a series: the number to use, and the highest name that exists (null when there is none). */
export interface ServerSequence {
  next: number
  highestExisting: string | null
}

/** The number after the highest existing one: the highest, not the first gap. 1 when none exist. */
export function nextSequence(names: readonly string[], series: Pick<ServerNameSeries, 'before' | 'after'>): ServerSequence {
  const highest = highestNumber(names, series)
  return { next: highest + 1, highestExisting: highest === 0 ? null : nameFor(series, highest) }
}

export const CONFIRMED_AT_DEPLOYMENT = 'The final name is confirmed when the deployment is created.'

/**
 * The names for a series and its sequence (`count` consecutive ones: the session hosts of one RDS server), with the note that says how the
 * number was chosen; or why they cannot be used. A name over 15 characters is refused, never shortened.
 */
export function serverNameFromSequence(
  series: ServerNameSeries,
  sequence: ServerSequence,
  count = 1,
): { ok: true; name: string; names: string[]; note: string } | { ok: false; problem: string } {
  const names = Array.from({ length: Math.max(count, 1) }, (_, index) => nameFor(series, sequence.next + index))
  const tooLong = names.find((name) => name.length > HOST_NAME_MAX)
  if (tooLong) return { ok: false, problem: `${tooLong} is ${tooLong.length} characters, more than the ${HOST_NAME_MAX} a server host name may have. It is not shortened.` }
  const how = sequence.highestExisting
    ? `Next number after ${sequence.highestExisting}.`
    : `No ${series.before}… VM exists yet, so this is number ${pad(sequence.next)}.`
  return { ok: true, name: names[0], names, note: `Pattern ${series.pattern}. ${how} ${CONFIRMED_AT_DEPLOYMENT}` }
}

/** How a sequence is kept: by environment and series (the same series in two environments is two subscriptions). */
export const sequenceKey = (environmentId: string, series: Pick<ServerNameSeries, 'before' | 'after'>): string => `${environmentId}:${series.before}|${series.after}`
